// File: src/pages/FansPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import app from '../services/firebaseConfig';
import UserProfilePicture from '../components/profile/UserProfilePicture';
import FanLeaderboardModal from '../components/fans/FanLeaderboard';
import ArtistFansModal from '../components/fans/ArtistFansModal';
import LeaderboardScoringModal from '../components/fans/LeaderboardScoringModal';
import { useNavigate } from 'react-router-dom';

import { BADGE_TIERS } from '../config/constants';

const db = getFirestore(app);

/**
 * Helper: Formats snapshot date into a friendly "As of Today/Yesterday, [time]" string
 */
function formatSnapshotDate(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();

  const isToday = 
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = 
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  const timeString = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).toLowerCase();

  if (isToday) return `As of Today, ${timeString}`;
  if (isYesterday) return `As of Yesterday, ${timeString}`;

  return `As of ${date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${timeString}`;
}

const FansPage = () => {
  const [viewToggle, setViewToggle] = useState('artists'); // 'artists' or 'fans'
  const [timeFrame, setTimeFrame] = useState('this_month'); // 'this_month' or 'all_time'
  
  const [loading, setLoading] = useState(true);
  const [isScoringInfoOpen, setIsScoringInfoOpen] = useState(false);
  const [leaderboardCache, setLeaderboardCache] = useState({ 
    topArtists: [], 
    topArtistsAllTime: [],
    topArtistsThisMonth: [],
    topFans: [], 
    topFansAllTime: [], 
    topFansThisMonth: [] 
  });
  
  const [activeModalArtistId, setActiveModalArtistId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const navigate = useNavigate();

  // Memoized Artist List
  const displayedArtists = useMemo(() => {
    if (timeFrame === 'this_month') {
      return leaderboardCache.topArtistsThisMonth?.length > 0 
        ? leaderboardCache.topArtistsThisMonth 
        : leaderboardCache.topArtistsAllTime || leaderboardCache.topArtists || [];
    }
    return leaderboardCache.topArtistsAllTime || leaderboardCache.topArtists || [];
  }, [timeFrame, leaderboardCache]);

  // Memoized Fan List (Filters out owner accounts)
  const displayedFans = useMemo(() => {
    const rawList = timeFrame === 'this_month' 
      ? (leaderboardCache.topFansThisMonth?.length > 0 ? leaderboardCache.topFansThisMonth : leaderboardCache.topFansAllTime || leaderboardCache.topFans || [])
      : (leaderboardCache.topFansAllTime || leaderboardCache.topFans || []);

    return rawList.filter((fan) => {
      const isOwner = fan.isOwner === true || (fan.userId && fan.artistId && fan.userId === fan.artistId);
      return !isOwner;
    });
  }, [timeFrame, leaderboardCache]);

  const handleOpenFans = (artist) => {
    setSelectedArtist(artist);
    setIsModalOpen(true);
  };

  const handleArtistClick = (e, artistId) => {
    e.stopPropagation();
    if (artistId) {
      navigate(`/profile/${artistId}`);
    }
  };

  useEffect(() => {
    const cacheDocRef = doc(db, 'fan_leaderboards', 'fan_hub_v1');
    
    const unsubscribe = onSnapshot(cacheDocRef, (snapshot) => {
      if (snapshot.exists()) {
        setLeaderboardCache(snapshot.data());
      }
      setLoading(false);
    }, (error) => {
      console.error("Error streaming global cache snapshot:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 px-4 pt-4 pb-16 transition-colors duration-200">
      <div className="max-w-3xl mx-auto space-y-6">
        
        {/* 🌟 1. STYLIZED HEADER BOX AT THE TOP */}
        <header className="w-full rounded-[2rem] border transition-all bg-white dark:bg-zinc-900/60 border-slate-200/80 dark:border-zinc-800 shadow-xl dark:shadow-2xl relative overflow-hidden p-6 md:p-8 text-center space-y-4">
          
          {/* Top Pink to Blue Accent Gradient Bar */}
          <div className="absolute top-0 left-0 right-0 h-[4px] bg-gradient-to-r from-brand-pink via-purple-500 to-blue-500" />

          {/* Title & Smooth Timestamp */}
          <div>
            <h1 className="text-3xl md:text-4xl font-black italic uppercase tracking-tight text-slate-900 dark:text-white">
              Fans
            </h1>
            {leaderboardCache.lastUpdated && (
              <p className="text-xs font-medium text-slate-400 dark:text-zinc-400 mt-1 antialiased tracking-normal">
                {formatSnapshotDate(leaderboardCache.lastUpdated)}
              </p>
            )}
          </div>

          {/* Controls: Primary and Secondary Toggles */}
          <div className="flex flex-col items-center justify-center gap-3 pt-1">
            
            {/* Primary Toggle: Top Artists vs Top Fans */}
            <div className="bg-slate-100 dark:bg-slate-900/90 p-1 rounded-2xl flex gap-1 h-12 items-center shadow-inner w-full max-w-xs border border-slate-200 dark:border-slate-800">
              <button 
                onClick={() => setViewToggle('artists')}
                className={`flex-1 h-10 text-xs font-black uppercase tracking-wider rounded-xl transition-all duration-150 ${
                  viewToggle === 'artists' ? 'bg-[#ff00cc] text-white shadow-md scale-[1.02]' : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                Top Artists
              </button>
              <button 
                onClick={() => setViewToggle('fans')}
                className={`flex-1 h-10 text-xs font-black uppercase tracking-wider rounded-xl transition-all duration-150 ${
                  viewToggle === 'fans' ? 'bg-[#ff00cc] text-white shadow-md scale-[1.02]' : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                Top Fans
              </button>
            </div>

            {/* Secondary Toggle: This Month vs All-Time + Info Button on the Right */}
            <div className="flex items-center gap-2">
              <div className="bg-slate-100/70 dark:bg-slate-900/60 p-0.5 rounded-xl flex gap-1 h-8 items-center max-w-[210px] border border-slate-200/80 dark:border-slate-800/80">
                <button 
                  onClick={() => setTimeFrame('this_month')}
                  className={`flex-1 h-7 px-3 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all duration-150 ${
                    timeFrame === 'this_month' 
                      ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-xs' 
                      : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                  }`}
                >
                  This Month
                </button>
                <button 
                  onClick={() => setTimeFrame('all_time')}
                  className={`flex-1 h-7 px-3 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all duration-150 ${
                    timeFrame === 'all_time' 
                      ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-xs' 
                      : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                  }`}
                >
                  All-Time
                </button>
              </div>

              {/* 🌟 (i) Info Icon positioned directly to the right */}
              <button
                type="button"
                onClick={() => setIsScoringInfoOpen(true)}
                aria-label="Explain how scoring works"
                className="w-6 h-6 shrink-0 rounded-full border border-slate-300 dark:border-slate-700 flex items-center justify-center text-[10px] font-black text-slate-400 hover:text-[#ff00cc] hover:border-[#ff00cc] transition-colors"
              >
                i
              </button>
            </div>

          </div>
        </header>

        {/* 🌟 2. LIST DISPLAY */}
        {loading ? (
          <div className="text-center py-20 text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">
            Loading fans snapshot...
          </div>
        ) : (
          <div className="space-y-2.5">
            
            {/* VIEW A: TOP ARTISTS */}
            {viewToggle === 'artists' && (
              displayedArtists.length === 0 ? (
                <div className="text-center py-12 text-sm text-slate-400 font-bold">
                  No Fan point activity recorded for this period.
                </div>
              ) : (
                displayedArtists.map((artist, idx) => {
                  const topFanScore = Number(artist.topFanScore) || 0;

                  const resolvedBadgeTier = topFanScore >= BADGE_TIERS.ULTRAFAN.minPoints 
                    ? 'ULTRAFAN' 
                    : topFanScore >= BADGE_TIERS.SUPAFAN.minPoints 
                      ? 'SUPAFAN' 
                      : 'FAN';

                  return (
                    <div 
                      key={artist.id || idx} 
                      className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800/60 shadow-2sm gap-2"
                    >
                      {/* Left Side: Rank, Avatar & Info (min-w-0 prevents layout overflow) */}
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="w-5 font-black text-xs text-slate-400 tabular-nums text-center shrink-0">
                          {idx + 1}
                        </span>
                        
                        <div 
                          onClick={(e) => handleArtistClick(e, artist.id)} 
                          className="cursor-pointer hover:scale-105 transition-transform shrink-0"
                        >
                          <UserProfilePicture photoURL={artist.avatar} sizeClass="w-10 h-10" />
                        </div>

                        {/* Text Container with Truncation on Mobile */}
                        <div className="min-w-0 flex-1 pr-1">
                          <h4 
                            onClick={(e) => handleArtistClick(e, artist.id)}
                            className="font-black text-sm text-slate-800 dark:text-slate-200 leading-tight truncate cursor-pointer hover:text-[#ff00cc] transition-colors"
                            title={artist.artistName}
                          >
                            {artist.artistName}
                          </h4>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mt-0.5 whitespace-nowrap">
                            {(Number(artist.score) || 0).toLocaleString()} Points Earned
                          </p>
                        </div>
                      </div>

                      {/* Right Side: Top Fan Indicator (shrink-0 prevents image from being pushed outside) */}
                      <div 
                        onClick={() => handleOpenFans(artist)}
                        className="flex flex-col items-center text-center justify-center cursor-pointer hover:opacity-80 transition-opacity min-w-[70px] sm:min-w-[85px] shrink-0 ml-auto"
                      >
                        <span className="text-[10px] font-black uppercase tracking-wider mb-1 block w-full leading-[1.15]">
                          {topFanScore >= BADGE_TIERS.ULTRAFAN.minPoints ? (
                            <span className="flex flex-col items-center">
                              <span className="text-[#ff00cc] text-[9px]">Top</span>
                              <span className="text-[#ff00cc]">UltraFan</span>
                            </span>
                          ) : topFanScore >= BADGE_TIERS.SUPAFAN.minPoints ? (
                            <span className="flex flex-col items-center">
                              <span className="text-[#3b82f6] text-[9px]">Top</span>
                              <span className="text-[#3b82f6]">SupaFan</span>
                            </span>
                          ) : (
                            <span className="text-[#22c55e]">Top Fan</span>
                          )}
                        </span>

                        <UserProfilePicture 
                          photoURL={artist.topFanAvatar} 
                          sizeClass="w-10 h-10" 
                          badgeTier={resolvedBadgeTier}
                        />
                      </div>
                    </div>
                  );
                })
              )
            )}

            {/* VIEW B: TOP FANS */}
            {viewToggle === 'fans' && (
              displayedFans.length === 0 ? (
                <div className="text-center py-12 text-sm text-slate-400 font-bold">
                  No supporter point activity recorded for this period.
                </div>
              ) : (
                displayedFans.map((fan, idx) => {
                  const handleFanClick = (e) => {
                    e.stopPropagation();
                    if (fan.userId) {
                      navigate(`/profile/${fan.userId}`);
                    }
                  };

                  const handleArtistSubClick = (e) => {
                    e.stopPropagation();
                    if (fan.artistId) {
                      navigate(`/profile/${fan.artistId}`);
                    }
                  };

                  const fanScore = Number(fan.score) || Number(fan.totalPointsTipped) || 0;
                  const resolvedArtistAvatar = fan.artistAvatar || fan.avatar || "";

                  const resolvedFanTier = fanScore >= BADGE_TIERS.ULTRAFAN.minPoints 
                    ? 'ULTRAFAN' 
                    : fanScore >= BADGE_TIERS.SUPAFAN.minPoints 
                      ? 'SUPAFAN' 
                      : 'FAN';
                  
                  return (
                    <div 
                      key={fan.id || idx} 
                      className="flex items-center justify-between p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800/60 shadow-2sm gap-2"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="w-5 font-black text-xs text-slate-400 tabular-nums text-center shrink-0">
                          {idx + 1}
                        </span>
                        
                        <div onClick={handleFanClick} className="cursor-pointer hover:scale-105 transition-transform shrink-0">
                          <UserProfilePicture 
                            photoURL={fan.fanAvatar} 
                            sizeClass="w-10 h-10" 
                            badgeTier={resolvedFanTier}
                          />
                        </div>

                        <div className="min-w-0 flex-1 pr-1">
                          <h4 
                            onClick={handleFanClick}
                            className="font-black text-sm text-slate-800 dark:text-slate-200 leading-tight truncate cursor-pointer hover:text-[#ff00cc] transition-colors"
                            title={fan.fanName}
                          >
                            {fan.fanName || "Anonymous Fan"}
                          </h4>
                          
                          <p className="text-[10px] font-black uppercase tracking-wider block leading-none mt-0.5">
                            {resolvedFanTier === 'ULTRAFAN' ? (
                              <span className="text-[#ff00cc]">UltraFan</span>
                            ) : resolvedFanTier === 'SUPAFAN' ? (
                              <span className="text-[#3b82f6]">SupaFan</span>
                            ) : (
                              <span className="text-[#22c55e]">Fan</span>
                            )}
                          </p>
                          
                          <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider block leading-none tabular-nums mt-1 whitespace-nowrap">
                            {fanScore.toLocaleString()} Points Tipped
                          </p>
                        </div>
                      </div>

                      {/* Associated Artist Column */}
                      <div 
                        onClick={handleArtistSubClick}
                        className="flex flex-col items-center justify-center cursor-pointer hover:opacity-80 transition-opacity min-w-[70px] sm:min-w-[85px] shrink-0 ml-auto"
                      >
                        <span className="text-[10px] font-black uppercase tracking-wider mb-1.5 text-center block w-full leading-tight text-slate-700 dark:text-slate-300 max-w-[75px] sm:max-w-[85px] truncate">
                          {fan.artistName || "Creator"}
                        </span>

                        <UserProfilePicture 
                          photoURL={resolvedArtistAvatar} 
                          sizeClass="w-10 h-10" 
                        />
                      </div>
                    </div>
                  );
                })
              )
            )}

          </div>
        )}
      </div>

      {/* OVERLAY MODALS */}
      {activeModalArtistId && (
        <FanLeaderboardModal 
          artistId={activeModalArtistId} 
          isOpen={true} 
          onClose={() => setActiveModalArtistId(null)} 
        />
      )}

      <ArtistFansModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        artistId={selectedArtist?.id}
        artistName={selectedArtist?.artistName}
      />

      <LeaderboardScoringModal 
        isOpen={isScoringInfoOpen}
        onClose={() => setIsScoringInfoOpen(false)}
        isDarkMode={document.documentElement.classList.contains('dark')}
      />
    </div>
  );
};

export default FansPage;